// assets/footer-app-triggers.js
/**
 * CUSTOM (HOMYCASA): lets a footer or header block open a third-party chat app
 * instead of navigating, by carrying one of the custom classes registered in
 * `TRIGGERS`.
 *
 * To wire up another app, append an entry with the block's custom class and an
 * `open()` that returns `true` once it has handled the click. `open()` receives
 * the triggering element, so it can look at ancestors (e.g. to close a dialog
 * it lives inside).
 */

/**
 * MooseDesk WhatsApp mounts a React tree inside `<section id="md-wa-app">`. The
 * element that owns the click handler is the launcher container, tried in order
 * below — the wrappers around them do not carry the handler, so a single grouped
 * selector would match an ancestor and click nothing.
 */
const WHATSAPP_APP_SELECTOR = '#md-wa-app';
const WHATSAPP_LAUNCHER_SELECTORS = [
  '.wa-corner-button-container',
  '.wa-float-side-button-container',
  '.wa-custom-logo-btn',
  '.wa-custom-logo-image',
];

/**
 * Fallback for pages where the WhatsApp embed is not rendered (the app can be
 * limited to a subset of pages/devices). Mirrors the URL the app itself builds
 * from `window.waConfigs`, so the number stays configured in a single place.
 */
function openWhatsAppDirectly() {
  const settings = window.waConfigs?.default_settings;
  const phone = settings?.whatsAppPhoneNumber?.replace(/\D/g, '');

  if (!phone) return false;

  const isMobile = window.matchMedia('(pointer: coarse)').matches;
  const url =
    settings.whatsAppType === 'web'
      ? `https://${isMobile ? 'api' : 'web'}.whatsapp.com/send?phone=${phone}`
      : `https://wa.me/${phone}`;

  window.open(url, '_blank', 'noopener');

  return true;
}

/**
 * Forwarding a synthetic `click()` from inside the real user click keeps the
 * user activation alive, so the `window.open` the app performs is not swallowed
 * by the popup blocker.
 */
function openWhatsApp() {
  const app = document.querySelector(WHATSAPP_APP_SELECTOR);

  for (const selector of WHATSAPP_LAUNCHER_SELECTORS) {
    const launcher = app?.querySelector(selector);

    if (launcher instanceof HTMLElement) {
      launcher.click();
      return true;
    }
  }

  return openWhatsAppDirectly();
}

const CHAT_SELECTOR = 'shopify-chat';
const CHAT_VISIBLE_ATTRIBUTE = 'data-homycasa-visible';

/**
 * The chat host is hidden by default (see base-homycasa.css) so its floating
 * activator never shows up on its own. Mirroring the host's `open` attribute
 * keeps it visible for exactly as long as the panel is open — including when
 * the component restores an ongoing conversation by itself after a navigation,
 * and hiding it again once the visitor closes the panel.
 */
function watchShopifyChat() {
  const chat = document.querySelector(CHAT_SELECTOR);

  if (!(chat instanceof HTMLElement) || chat.dataset.homycasaWatched === 'true') return;

  chat.dataset.homycasaWatched = 'true';

  const sync = () => chat.toggleAttribute(CHAT_VISIBLE_ATTRIBUTE, chat.hasAttribute('open'));

  new MutationObserver(sync).observe(chat, { attributeFilter: ['open'] });
  sync();
}

/**
 * Shopify Inbox renders the `<shopify-chat>` storefront web component, which
 * exposes `show()` once upgraded. Before the upgrade the `open` attribute is
 * read on connect, so it works as a declarative fallback.
 */
function openShopifyChat() {
  const chat = document.querySelector(CHAT_SELECTOR);

  if (!(chat instanceof HTMLElement)) return false;

  watchShopifyChat();

  // Reveal before opening: `show()` measures the panel, and a `display: none`
  // host would measure zero.
  chat.toggleAttribute(CHAT_VISIBLE_ATTRIBUTE, true);

  if (typeof chat.show === 'function') {
    chat.show();
  } else {
    chat.setAttribute('open', '');
  }

  return true;
}

/**
 * CUSTOM (HOMYCASA): the header's `.header-inbox-link` trigger lives inside a
 * `popup-link` dialog, unlike the footer's `.footer-inbox-link`. A native
 * `<dialog>` opened as modal makes everything outside it inert, so Shopify
 * Inbox can't be interacted with while it's open — the dialog has to close
 * first, then the chat opens. The short delay gives the dialog's own closing
 * animation/inert-cleanup a moment to finish.
 */
function closeHeaderDialogThenOpenShopifyChat(element) {
  const dialogComponent = element.closest('dialog-component');

  if (typeof dialogComponent?.closeDialog === 'function') {
    dialogComponent.closeDialog();
  } else {
    dialogComponent?.querySelector('dialog')?.close();
  }

  setTimeout(openShopifyChat, 50);

  return true;
}

const TRIGGERS = [
  { selector: '.footer-whatsapp-link', open: () => openWhatsApp() },
  { selector: '.footer-inbox-link', open: () => openShopifyChat() },
  { selector: '.header-inbox-link', open: (element) => closeHeaderDialogThenOpenShopifyChat(element) },
];

const TRIGGER_SELECTOR = TRIGGERS.map(({ selector }) => selector).join(',');

/**
 * @param {EventTarget | null} eventTarget
 * @returns {{ element: HTMLElement, open: (element: HTMLElement) => boolean } | null}
 */
function findTrigger(eventTarget) {
  const element = eventTarget instanceof Element ? eventTarget.closest(TRIGGER_SELECTOR) : null;

  if (!(element instanceof HTMLElement)) return null;

  const trigger = TRIGGERS.find(({ selector }) => element.matches(selector));

  return trigger ? { element, open: trigger.open } : null;
}

/** Group blocks render as a plain `<div>`, so expose them as a button. */
function markTriggers() {
  for (const element of document.querySelectorAll(TRIGGER_SELECTOR)) {
    if (element.dataset.appTrigger === 'true') continue;

    element.dataset.appTrigger = 'true';
    element.setAttribute('role', 'button');
    element.setAttribute('tabindex', '0');
  }
}

document.addEventListener('click', (event) => {
  const trigger = findTrigger(event.target);

  if (!trigger) return;

  event.preventDefault();
  trigger.open(trigger.element);
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Enter' && event.key !== ' ') return;

  const trigger = findTrigger(event.target);

  if (!trigger) return;

  event.preventDefault();
  trigger.open(trigger.element);
});

markTriggers();
watchShopifyChat();
document.addEventListener('shopify:section:load', markTriggers);